import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { EGoogleAdsKeywordMatchType } from '@feathr/blackbox';
import { Button, Input, UniversalSelect } from '@feathr/components';

import type { IAdGroupKeyword } from '../AdStep.types';
import type { IMatchTypeOption } from './KeywordFields.utils';

interface IKeywordRowProps {
  keyword: IAdGroupKeyword;
  /** 1-based position, which names the row's controls. */
  number: number;
  /** This keyword's messages, if it has any. */
  errors?: string[];
  onTextChange: (index: number, text: string) => void;
  onRemove: (index: number) => void;
  /**
   * Omit for negative keywords, which carry no match type. Supplying it adds the picker
   * column, so the row matches whichever grid it is rendered into.
   */
  matchTypeOptions?: IMatchTypeOption[];
  onMatchTypeChange?: (index: number, matchType: EGoogleAdsKeywordMatchType) => void;
  disabled?: boolean;
}

/**
 * One keyword as an editable row: its text, optionally the match type it competes on, and a
 * control to drop it. The cells are returned loose rather than wrapped, so the enclosing
 * grid lines every row up against the headers naming its columns.
 *
 * The row owns when its message is shown, which is not simply whenever there is one:
 *
 * - Not while it is being typed into. Every multi-word keyword is a single word on the way
 *   to being typed, and the policy complaint would arrive after the first one.
 * - Not on a blank row nobody has been to yet. Rows are added empty on purpose, and one
 *   that has just appeared has not done anything wrong.
 * - Otherwise yes, including on a row this session never touched — the duplicate rule
 *   reports against the *later* of two matching rows, which is often not the one being
 *   edited, and a message nobody can see still blocks the campaign from publishing.
 *
 * @param props - {@link IKeywordRowProps}.
 */
function KeywordRow({
  keyword,
  number,
  errors,
  onTextChange,
  onRemove,
  matchTypeOptions,
  onMatchTypeChange,
  disabled = false,
}: Readonly<IKeywordRowProps>): JSX.Element {
  const { t } = useTranslation();
  const [isTyping, setIsTyping] = useState(false);
  const [hasBeenVisited, setHasBeenVisited] = useState(false);

  const index = number - 1;
  const isBlank = !keyword.text.trim();
  const isShowingErrors = !isTyping && (hasBeenVisited || !isBlank);
  const shownErrors = isShowingErrors && errors?.length ? errors : undefined;

  function handleTextChange(value?: string): void {
    setIsTyping(true);
    onTextChange(index, value ?? '');
  }

  function handleBlur(): void {
    setIsTyping(false);
    setHasBeenVisited(true);
  }

  function handleMatchTypeChange(option: IMatchTypeOption): void {
    onMatchTypeChange?.(index, option.id);
  }

  function handleRemove(): void {
    onRemove(index);
  }

  /* Negative keywords are named for what they do, so their controls say so. */
  const labels = matchTypeOptions
    ? {
        field: t('Keyword {{number}}', { number }),
        matchType: t('Match type for keyword {{number}}', { number }),
        placeholder: t('Keyword or phrase'),
        remove: t('Remove keyword {{number}}', { number }),
        removeName: `remove-keyword-${number}`,
        removeTooltip: t('Remove keyword'),
      }
    : {
        field: t('Negative keyword {{number}}', { number }),
        matchType: '',
        placeholder: t('Term to exclude'),
        remove: t('Remove negative keyword {{number}}', { number }),
        removeName: `remove-negative-keyword-${number}`,
        removeTooltip: t('Remove negative keyword'),
      };

  return (
    <Fragment>
      <Input
        aria-label={labels.field}
        disabled={disabled}
        /* Input holds a message back until the field is touched; this row decides instead. */
        initializeFocused={!!shownErrors}
        onBlur={handleBlur}
        onChange={handleTextChange}
        placeholder={labels.placeholder}
        type={'text'}
        validationError={shownErrors}
        value={keyword.text}
      />
      {!!matchTypeOptions && (
        <UniversalSelect<IMatchTypeOption>
          aria-label={labels.matchType}
          disabled={disabled}
          name={`keyword-match-type-${number}`}
          onSelectSingle={handleMatchTypeChange}
          options={matchTypeOptions}
          value={matchTypeOptions.find((option) => option.id === keyword.matchType)}
        />
      )}
      <Button
        aria-label={labels.remove}
        disabled={disabled}
        name={labels.removeName}
        onClick={handleRemove}
        tooltip={labels.removeTooltip}
        type={'icon'}
      >
        <FontAwesomeIcon icon={faTrash} />
      </Button>
    </Fragment>
  );
}

export default KeywordRow;
